import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { useSwipeGesture } from "./useSwipeGesture";
import { useBoundStore } from "@/app/store";
import { useShallow } from "zustand/shallow";

interface UseSwipeBackOptions {
  /**
   * Whether to enable swipe back navigation
   * @default true
   */
  enabled?: boolean;

  /**
   * Minimum distance in pixels to trigger navigation
   * @default 50
   */
  threshold?: number;

  /**
   * Edge zone width in pixels where swipe can start
   * @default 20
   */
  edgeZone?: number;
}

export function useSwipeBack(options: UseSwipeBackOptions = {}) {
  const { isNativeDevice, devicePlatform } = useBoundStore(
    useShallow((state) => ({ isNativeDevice: state.device.isNative, devicePlatform: state.device.platform }))
  );
  const { enabled = true, threshold = 50, edgeZone = 20 } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const navigationHistoryRef = useRef<string[]>([]);
  const isNavigatingRef = useRef(false);

  // Track navigation history
  useEffect(() => {
    if (!isNavigatingRef.current) {
      navigationHistoryRef.current.push(location.pathname);
    }
    isNavigatingRef.current = false;
  }, [location.pathname]);

  // Determine if we can go back
  const canNavigateBack = useCallback(() => {
    // Check if we have history
    return navigationHistoryRef.current.length > 1;
  }, []);

  const handleSwipeBack = useCallback(() => {
    if (!canNavigateBack()) {
      return;
    }

    // Perform navigation
    isNavigatingRef.current = true;
    navigate(-1);

    // Update history
    navigationHistoryRef.current.pop();
  }, [canNavigateBack, navigate]);

  // Use the swipe gesture hook
  const swipeState = useSwipeGesture({
    enabled: enabled && isNativeDevice && devicePlatform === "ios",
    threshold,
    edgeZone,
    onSwipeBack: handleSwipeBack,
  });

  return {
    ...swipeState,
    fullPathname: location.pathname + location.search,
  };
}
